import React, { FC, useState, useCallback, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Upload, Youtube, Loader2, Copy, Check, ChevronDown } from 'lucide-react';
import { AppStateTypes } from '../../store/appStateTypes';
import { axiosInstance } from '../../helpers/axiosInstance';
import { useModal } from '../../hooks/useModal';
import { MODAL_NAME_AUTH } from '../../constants/modalNamesConstants';
import { SubscriptionContainer } from '../subscription/SubscriptionContainer';

export type RecognitionType = 'photo' | 'audio' | 'youtube' | 'document';

const SEO: Record<RecognitionType, { title: string; description: string; canonical: string }> = {
  photo: {
    title: 'Распознать текст с фото онлайн | Писака',
    description: 'Загрузите фото и получите текст онлайн. Распознаём рукописный и печатный текст с изображений. До 10 фото за раз.',
    canonical: 'https://pisaka.online/photo-to-text/',
  },
  audio: {
    title: 'Аудио в текст онлайн — расшифровка записей | Писака',
    description: 'Расшифруйте аудиозапись в текст онлайн. Поддерживаем MP3, M4A, WAV, OGG. Голосовые сообщения, лекции, интервью — в текст за минуты.',
    canonical: 'https://pisaka.online/audio-to-text/',
  },
  youtube: {
    title: 'YouTube видео в текст онлайн — транскрипт | Писака',
    description: 'Получите текстовый транскрипт YouTube видео онлайн. Вставьте ссылку на видео и получите расшифровку. Быстро и точно.',
    canonical: 'https://pisaka.online/youtube-to-text/',
  },
  document: {
    title: 'PDF в текст онлайн — распознать скан | Писака',
    description: 'Конвертируйте PDF и DOCX в текст онлайн. Распознаём сканы и цифровые документы. Скопируйте текст из любого PDF файла.',
    canonical: 'https://pisaka.online/pdf-to-text/',
  },
};

const ACCEPT: Record<RecognitionType, string> = {
  photo: 'image/*',
  audio: '.mp3,.m4a,.ogg,.wav,.webm,.flac',
  youtube: '',
  document: '.pdf,.docx',
};

interface Topic {
  id: string;
  title: string;
  createdAt: string;
  status?: string;
  sourceMeta?: { type: string };
}

export const RecognitionPage: FC<{ type: RecognitionType }> = ({ type }) => {
  const { token } = useSelector((s: AppStateTypes) => s.auth);
  const { onOpenModal: openAuthModal } = useModal(MODAL_NAME_AUTH);
  const { t } = useTranslation();

  const [files, setFiles] = useState<File[]>([]);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [subModal, setSubModal] = useState(false);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [openTopicId, setOpenTopicId] = useState<string | null>(null);
  const [topicTexts, setTopicTexts] = useState<Record<string, string>>({});
  const [copiedTopicId, setCopiedTopicId] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const seo = SEO[type];

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const loadTopics = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axiosInstance(token).get('/api/study/topics');
      const filtered = (res.data.topics as Topic[]).filter(t => t.sourceMeta?.type === type);
      setTopics(filtered);
    } catch {}
  }, [token, type]);

  useEffect(() => { loadTopics(); }, [loadTopics]);

  // Поллинг списка пока есть processing — та же схема что в FlashcardsModule
  useEffect(() => {
    const hasProcessing = topics.some(t => t.status === 'processing');
    if (!hasProcessing) { stopPolling(); return; }
    if (pollRef.current) return;
    pollRef.current = setInterval(loadTopics, 3000);
    return stopPolling;
  }, [topics, loadTopics]);

  const handleRecognize = async () => {
    if (!token) { openAuthModal(); return; }
    if (type !== 'youtube' && !files.length) return;
    if (type === 'youtube' && !youtubeUrl.trim()) return;

    setIsSubmitting(true); setError('');
    try {
      let topicId: string;
      if (type === 'youtube') {
        const r = await axiosInstance(token).post('/api/study/recognize/youtube', { url: youtubeUrl });
        topicId = r.data.topicId;
      } else {
        const fd = new FormData();
        if (type === 'photo') files.forEach(f => fd.append('images', f));
        else fd.append('file', files[0]);
        const endpoint = type === 'photo' ? '/api/study/recognize/photo'
          : type === 'audio' ? '/api/study/recognize/audio'
          : '/api/study/recognize/document';
        const r = await axiosInstance(token).post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        topicId = r.data.topicId;
      }
      // Сохраняем в общий ключ — главная страница тоже подхватит лоадер
      localStorage.setItem('recognition_job', JSON.stringify({ topicId, tab: type, startedAt: Date.now() }));
      setFiles([]); setYoutubeUrl('');
      await loadTopics();
    } catch (e: any) {
      const msg: string = e?.response?.data?.error?.message ?? e?.message ?? 'Ошибка';
      if (msg.toLowerCase().includes('токен') || e?.response?.status === 400) {
        setSubModal(true);
      } else {
        setError(msg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleTopic = async (id: string) => {
    if (openTopicId === id) { setOpenTopicId(null); return; }
    setOpenTopicId(id);
    if (topicTexts[id] !== undefined) return;
    try {
      const res = await axiosInstance(token).get(`/api/study/topics/${id}`);
      setTopicTexts(prev => ({ ...prev, [id]: res.data.sourceText ?? '' }));
    } catch {
      setTopicTexts(prev => ({ ...prev, [id]: '' }));
    }
  };

  const handleCopyTopic = (id: string) => {
    navigator.clipboard.writeText(topicTexts[id] ?? '');
    setCopiedTopicId(id);
    setTimeout(() => setCopiedTopicId(null), 2000);
  };

  const isProcessing = topics.some(t => t.status === 'processing');
  const canSubmit = type === 'youtube' ? !!youtubeUrl.trim() : files.length > 0;

  const buttonLabel = {
    photo: t('recognitionPage.buttonPhoto'),
    audio: t('recognitionPage.buttonAudio'),
    youtube: t('recognitionPage.buttonYoutube'),
    document: t('recognitionPage.buttonDocument'),
  }[type];

  const fileLabel = type === 'photo'
    ? `${files.length} ${files.length === 1 ? t('recognitionPage.fileSelected') : t('recognitionPage.filesSelected')}`
    : files[0]?.name ?? '';

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={seo.canonical} />
      </Helmet>

      <div className="min-h-screen bg-white pt-60 pb-16 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {({ photo: t('recognitionPage.photoCard'), audio: t('recognitionPage.audioCard'), youtube: t('recognitionPage.youtubeCard'), document: t('recognitionPage.documentCard') })[type]}
            </h1>
            <p className="text-gray-500 text-base">
              {({ photo: t('recognitionPage.photoCardDesc'), audio: t('recognitionPage.audioCardDesc'), youtube: t('recognitionPage.youtubeCardDesc'), document: t('recognitionPage.documentCardDesc') })[type]}
            </p>
          </div>

          {type === 'youtube' ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-8 mb-4">
              <div className="flex items-center gap-2 mb-4">
                <Youtube className="w-5 h-5 text-red-500" />
                <span className="text-sm font-medium text-gray-600">{t('recognitionPage.youtubeUrlLabel')}</span>
              </div>
              <input
                type="url"
                value={youtubeUrl}
                onChange={e => setYoutubeUrl(e.target.value)}
                placeholder={t('recognitionPage.youtubePlaceholder')}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          ) : (
            <div
              onClick={() => !isProcessing && !isSubmitting && inputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={e => {
                e.preventDefault(); setIsDragging(false);
                const f = Array.from(e.dataTransfer.files);
                setFiles(type === 'photo' ? f.slice(0, 10) : [f[0]]);
              }}
              className={`bg-white rounded-2xl border-2 border-dashed p-14 mb-4 text-center transition-colors ${isProcessing || isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${isDragging ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
            >
              <input
                ref={inputRef}
                type="file"
                accept={ACCEPT[type]}
                multiple={type === 'photo'}
                className="hidden"
                onChange={e => {
                  const f = Array.from(e.target.files ?? []);
                  setFiles(type === 'photo' ? f.slice(0, 10) : [f[0]]);
                }}
              />
              <Upload className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              {files.length > 0 ? (
                <p className="text-sm font-medium text-gray-700">{fileLabel}</p>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-1">
                    {type === 'photo' ? t('recognitionPage.photoLabel')
                      : type === 'audio' ? t('recognitionPage.audioLabel')
                      : t('recognitionPage.documentLabel')}
                  </p>
                  <p className="text-xs text-gray-400">
                    {type === 'photo' ? t('recognitionPage.photoHint')
                      : type === 'audio' ? t('recognitionPage.audioHint')
                      : t('recognitionPage.documentHint')}
                  </p>
                </>
              )}
            </div>
          )}

          <button
            onClick={handleRecognize}
            disabled={isSubmitting || isProcessing || (!!token && !canSubmit)}
            className="w-full py-4 bg-gradient-accent hover:bg-accent shadow-accent text-white rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 text-sm"
          >
            {isSubmitting || isProcessing
              ? <><Loader2 className="w-4 h-4 animate-spin" />{t('recognitionPage.processing')}</>
              : buttonLabel}
          </button>

          {error && <p className="mt-3 text-sm text-red-500 text-center">{error}</p>}

          {topics.length > 0 && (
            <div className="mt-16">
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{t('recognitionPage.historyTitle')}</h2>
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                {topics.slice(0, 10).map((topic, i) => {
                  const isOpen = openTopicId === topic.id;
                  const processing = topic.status === 'processing';
                  const text = topicTexts[topic.id];
                  return (
                    <div key={topic.id} className={i > 0 ? 'border-t border-gray-100' : ''}>
                      <button
                        onClick={() => !processing && handleToggleTopic(topic.id)}
                        className={`w-full flex items-center justify-between px-4 py-3 text-sm text-gray-700 transition-colors ${processing ? 'cursor-default' : 'hover:bg-gray-50'}`}
                      >
                        <span className="truncate text-left">{topic.title}</span>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                          <span className="text-xs text-gray-400">{new Date(topic.createdAt).toLocaleDateString('ru')}</span>
                          {processing
                            ? <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                            : <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                          }
                        </div>
                      </button>
                      {isOpen && !processing && (
                        <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                          {text === undefined ? (
                            <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></div>
                          ) : (
                            <>
                              <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap max-h-64 overflow-y-auto">{text}</p>
                              <button
                                onClick={() => handleCopyTopic(topic.id)}
                                className="mt-3 flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                              >
                                {copiedTopicId === topic.id ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                                {copiedTopicId === topic.id ? t('recognitionPage.copied') : t('recognitionPage.copy')}
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <SubscriptionContainer isOpen={subModal} onClose={() => setSubModal(false)} context="tokens" />
    </>
  );
};
